/// <reference types="vite/client" />

/**
 * Simple cookie utilities for cross-subdomain auth sharing.
 *
 * The hub writes these cookies; other microsites (quality, etc.) read them.
 * No Supabase dependency — these are plain JWT + partner-key cookies.
 *
 * Set the VITE_COOKIE_SUFFIX env var (e.g. "_staging") to append a suffix
 * to cookie names, avoiding clashes when multiple environments share a domain.
 */

declare global {
  interface ImportMetaEnv {
    readonly VITE_COOKIE_SUFFIX?: string;
  }
}

const cookieSuffix: string = import.meta.env.VITE_COOKIE_SUFFIX ?? '';

export const AUTH_COOKIE = `artic_token${cookieSuffix}`;
export const REFRESH_COOKIE = `artic_refresh_token${cookieSuffix}`;
export const PARTNER_COOKIE = `artic_partner${cookieSuffix}`;

export interface AuthCookieOptions {
  /** Cookie domain (e.g. `.artic.works`). Omit for localhost / same-origin. */
  domain?: string;
}

/** ~400 days — matches the dual-storage session cookie max-age. */
const MAX_AGE = 34_560_000;

function isSecureContext(): boolean {
  return typeof location !== 'undefined' && location.protocol === 'https:';
}

function buildAttrs(maxAge: number, domain?: string): string {
  let attrs = `; path=/; max-age=${String(maxAge)}; SameSite=Lax`;
  if (domain) attrs += `; domain=${domain}`;
  if (isSecureContext()) attrs += '; Secure';
  return attrs;
}

/**
 * Expire any host-only shadow of `name`. A cookie set without a `domain`
 * attribute is stored host-only on the current hostname; that shadow
 * survives every domain-scoped write and wins `document.cookie` reads
 * (most-specific match), keeping a stale value alive past every refresh.
 * Sentry ARTIC-HUB-PZ — see DoctorPage.tsx `articCookieShadows`.
 */
function expireHostOnlyShadow(name: string): void {
  document.cookie = `${name}=${buildAttrs(0)}`;
}

// ── Writers ─────────────────────────────────────────────────────────

export function setAuthTokenCookie(token: string, options: AuthCookieOptions = {}): void {
  if (options.domain) expireHostOnlyShadow(AUTH_COOKIE);
  document.cookie = `${AUTH_COOKIE}=${encodeURIComponent(token)}${buildAttrs(MAX_AGE, options.domain)}`;
}

export function setRefreshTokenCookie(token: string, options: AuthCookieOptions = {}): void {
  if (options.domain) expireHostOnlyShadow(REFRESH_COOKIE);
  document.cookie = `${REFRESH_COOKIE}=${encodeURIComponent(token)}${buildAttrs(MAX_AGE, options.domain)}`;
}

export function setPartnerCookie(partnerKey: string, options: AuthCookieOptions = {}): void {
  if (options.domain) expireHostOnlyShadow(PARTNER_COOKIE);
  document.cookie = `${PARTNER_COOKIE}=${encodeURIComponent(partnerKey)}${buildAttrs(MAX_AGE, options.domain)}`;
}

export function clearAuthCookies(options: AuthCookieOptions = {}): void {
  const expire = buildAttrs(0, options.domain);
  document.cookie = `${AUTH_COOKIE}=${expire}`;
  document.cookie = `${REFRESH_COOKIE}=${expire}`;
  document.cookie = `${PARTNER_COOKIE}=${expire}`;
  if (options.domain) {
    expireHostOnlyShadow(AUTH_COOKIE);
    expireHostOnlyShadow(REFRESH_COOKIE);
    expireHostOnlyShadow(PARTNER_COOKIE);
  }
}

// ── Reader (any microsite) ──────────────────────────────────────────

/** Read a single cookie value by name. Returns `undefined` if absent. */
export function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`).exec(
    document.cookie,
  );
  return match?.[1] !== undefined ? decodeURIComponent(match[1]) : undefined;
}
